import { RankerAlgorithim, RankDirection, RankAlignment } from '@google/workflow-graph'
import classNames from 'classnames'
import { memo, useEffect, useRef } from 'react'

// Zone.js is required before importing the Workflow Graph
import 'zone.js'

import type { WorkflowGraphProps, LayoutOptions } from '@google/workflow-graph'
import type { ReactElement } from 'react'
import type { IIapGraph } from '~/types/iap'

import './styles.scss'

const NODE_VERTICAL_SPACING = 60
const NODE_HORIZONTAL_SPACING = 40

const DEFAULT_LAYOUT_OPTIONS: LayoutOptions = {
  edgeSeparation: 4,
  nodeSeparation: NODE_HORIZONTAL_SPACING,
  rankAlignment: RankAlignment.NONE,
  rankDirection: RankDirection.TOP_TO_BOTTOM,
  rankSeparation: NODE_VERTICAL_SPACING,
  ranker: RankerAlgorithim.NETWORK_SIMPLEX,
}

interface Props {
  className?: string
  graph: IIapGraph
  onNodeClick: (id?: string) => void
}

const WorkflowGraph = ({ className, graph, onNodeClick }: Props): ReactElement => {
  const graphRef = useRef()

  useEffect(() => {
    const graphComponent = graphRef.current as HTMLDivElement | undefined

    const onSelectedNodeChange = (event: any) => {
      if (event?.detail?.node) {
        onNodeClick(event.detail.node.id)
      } else {
        onNodeClick(undefined)
      }
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }

    if (graphComponent && graph) {
      const props: WorkflowGraphProps = {
        dagSpec: { meta: graph.state, skeleton: graph.skeleton },
        enableMinimap: true,
        enableToolbar: true,
        expandedMode: true,
        followNode: null,
        hoveredEdge: undefined,
        layout: DEFAULT_LAYOUT_OPTIONS,
        loading: false,
        optimizeForOrm: false,
        selectedNode: null,
        zoom: 1,
      }

      Object.assign(graphComponent, props)
      graphComponent.addEventListener('selectedNodeChange', onSelectedNodeChange)
      graphComponent.addEventListener('wheel', onWheel)
    }

    return () => {
      graphComponent?.removeEventListener('selectedNodeChange', onSelectedNodeChange)
      graphComponent?.removeEventListener('wheel', onWheel)
    }
  })

  return (
    <div className={classNames('workflow-graph-component', className)}>
      <google-workflow-graph ref={graphRef} />
    </div>
  )
}

export default memo(WorkflowGraph)
